import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  type Notification,
} from '../../services/api/notifications';

const NotificationItem = ({ notification }: { notification: Notification }) => {
  const queryClient = useQueryClient();

  const markAsReadMutation = useMutation({
    mutationFn: () => markNotificationAsRead(notification.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const handleClick = () => {
    if (!notification.isRead) {
      markAsReadMutation.mutate();
    }
  };

  const getNotificationLink = () => {
    switch (notification.type) {
      case 'like':
      case 'comment_like':
      case 'comment':
      case 'co_comment':
      case 'event_attendee_comment':
      case 'new_post':
        return `/posts/${notification.relatedEntityId}`;
      case 'event_comment':
      case 'event_reminder':
      case 'event_invite':
        return `/events/${notification.relatedEntityId}`;
      case 'message':
        return `/messages/${notification.relatedEntityId}`;
      default:
        return '/';
    }
  };

  const getNotificationIcon = () => {
    switch (notification.type) {
      case 'like':
      case 'comment_like':
        return '❤️';
      case 'comment':
      case 'co_comment':
      case 'event_comment':
      case 'event_attendee_comment':
        return '💬';
      case 'new_post':
        return '📝';
      case 'event_reminder':
        return '⏰';
      case 'event_invite':
        return '📅';
      case 'message':
        return '✉️';
      default:
        return '🔔';
    }
  };

  const timeAgo = (date: string) => {
    const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' år siden';

    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' måneder siden';

    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' dage siden';

    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' timer siden';

    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' minutter siden';

    return 'Lige nu';
  };

  return (
    <Link
      to={getNotificationLink()}
      onClick={handleClick}
      className={`block px-3 py-2 border-b hover:bg-gray-50 transition ${
        !notification.isRead ? 'bg-blue-50' : 'bg-white'
      }`}
    >
      <div className="flex items-center gap-2">
        <div className="text-lg flex-shrink-0 w-6 text-center">{getNotificationIcon()}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-tight">{notification.title}</p>
          <p className="text-gray-600 text-xs leading-tight">{notification.message}</p>
          <p className="text-gray-400 text-xs">{timeAgo(notification.createdAt)}</p>
        </div>
        {!notification.isRead && (
          <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
        )}
      </div>
    </Link>
  );
};

export const NotificationList = () => {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['notifications', { page }],
    queryFn: () => getNotifications(page, 20),
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Kunne ikke indlæse notifikationer</p>
      </div>
    );
  }

  const notifications = data?.notifications ?? [];
  const total = data?.total ?? notifications.length;
  const hasUnread = notifications.some((n) => !n.isRead);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b flex justify-between items-center">
        <h2 className="text-xl font-bold">Notifikationer</h2>
        {hasUnread && (
          <button
            onClick={() => markAllAsReadMutation.mutate()}
            disabled={markAllAsReadMutation.isPending}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Markér alle som læst
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>Ingen notifikationer endnu</p>
        </div>
      ) : (
        <>
          <div className="divide-y">
            {notifications.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </div>

          {total > page * 20 && (
            <div className="p-4 text-center border-t">
              <button
                onClick={() => setPage(page + 1)}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Indlæs flere
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
